// Ballot Lookup: live unit search + received/not-received filter + inline "mark as received".
// Scoped to the [data-ballot-lookup] container so it never collides with search_units.js.

function initBallotLookup() {
  const root = document.querySelector('[data-ballot-lookup]');
  if (!root) return;

  const searchUrl = root.dataset.searchUrl;
  const receiptTemplate = root.dataset.receiptUrlTemplate; // contains "UNIT_ID"
  const input = document.getElementById('bl-search-input');
  const results = document.getElementById('bl-results');
  const status = document.getElementById('bl-status');
  const validCountEl = document.getElementById('bl-valid-count');
  const filterButtons = Array.from(document.querySelectorAll('.bl-filter'));

  if (!input || !results) return;
  if (root.dataset.blInitialized === 'true') return; // guard against double-binding on turbolinks
  root.dataset.blInitialized = 'true';

  let currentFilter = 'all';
  let timeout = null;

  const csrfToken = () => {
    const el = document.querySelector('meta[name="csrf-token"]');
    return el ? el.content : '';
  };

  const esc = (value) => {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  };

  function setActiveFilter(filter) {
    currentFilter = filter;
    filterButtons.forEach((btn) => {
      const active = btn.dataset.filter === filter;
      btn.classList.toggle('bg-blue-600', active);
      btn.classList.toggle('text-white', active);
      btn.classList.toggle('border-blue-600', active);
      btn.classList.toggle('bg-white', !active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  function statusBadge(unit) {
    if (!unit.received) {
      return '<span class="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-2.5 py-0.5 text-xs font-medium">Not received</span>';
    }
    if (unit.status === 'Provisional') {
      return '<span class="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-xs font-medium">Received · Provisional</span>';
    }
    const number = unit.valid_number ? ` · #${esc(unit.valid_number)}` : '';
    return `<span class="inline-flex items-center rounded-full bg-green-100 text-green-800 px-2.5 py-0.5 text-xs font-medium">Received · Valid${number}</span>`;
  }

  function fieldRow(label, value) {
    if (!value) return '';
    return `<div class="text-sm text-slate-600"><span class="text-slate-400">${label}:</span> ${esc(value)}</div>`;
  }

  function buildCard(unit) {
    const card = document.createElement('div');
    card.id = `bl-unit-${unit.id}`;
    card.className = 'bg-white rounded-lg shadow p-4 flex flex-col gap-2';

    card.innerHTML = `
      <div class="flex items-start justify-between gap-3">
        <div class="font-semibold text-slate-900">${esc(unit.name) || '—'}</div>
        <div class="bl-badge shrink-0">${statusBadge(unit)}</div>
      </div>
      ${fieldRow('Unit', unit.identifier)}
      ${fieldRow('Unit street', unit.add_street)}
      ${fieldRow('Mailing street', unit.mail_add_str)}
      ${fieldRow('Email', unit.email)}
    `;

    if (!unit.received) {
      const actions = document.createElement('div');
      actions.className = 'bl-actions mt-1';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = 'Mark as Received';
      btn.className = 'block w-full text-center bg-blue-600 text-white px-4 py-3 rounded font-medium hover:bg-blue-700';
      btn.addEventListener('click', () => markReceived(unit.id, card, btn));
      actions.appendChild(btn);
      card.appendChild(actions);
    }

    return card;
  }

  function render(units) {
    results.innerHTML = '';
    if (!units.length) {
      status.textContent = 'No matching units.';
      return;
    }
    status.textContent = `${units.length} unit${units.length === 1 ? '' : 's'}`;
    units.forEach((unit) => results.appendChild(buildCard(unit)));
  }

  function runSearch() {
    const query = input.value.trim();
    if (query.length < 2 && currentFilter === 'all') {
      results.innerHTML = '';
      status.textContent = 'Type at least 2 characters, or pick a filter, to search.';
      return;
    }
    status.textContent = 'Searching…';
    const url = `${searchUrl}?query=${encodeURIComponent(query)}&filter=${encodeURIComponent(currentFilter)}`;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => render(data))
      .catch(() => { status.textContent = 'Error fetching results.'; });
  }

  function markReceived(unitId, card, btn) {
    btn.disabled = true;
    btn.textContent = 'Marking…';
    const url = receiptTemplate.replace('UNIT_ID', unitId);
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() }
    })
      .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body.error || 'Failed to mark as received');
        const badge = card.querySelector('.bl-badge');
        if (badge) {
          const number = body.valid_number ? ` · #${esc(body.valid_number)}` : '';
          badge.innerHTML = `<span class="inline-flex items-center rounded-full bg-green-100 text-green-800 px-2.5 py-0.5 text-xs font-medium">Received · Valid${number}</span>`;
        }
        const actions = card.querySelector('.bl-actions');
        if (actions) actions.remove();
        if (validCountEl && typeof body.units_received_valid !== 'undefined') {
          validCountEl.textContent = body.units_received_valid;
        }
      })
      .catch((e) => {
        btn.disabled = false;
        btn.textContent = 'Mark as Received';
        alert(e.message);
      });
  }

  input.addEventListener('input', () => {
    clearTimeout(timeout);
    timeout = setTimeout(runSearch, 300);
  });

  filterButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      setActiveFilter(btn.dataset.filter);
      runSearch();
    });
  });

  setActiveFilter('all');
}

document.addEventListener('turbolinks:load', initBallotLookup);
document.addEventListener('DOMContentLoaded', initBallotLookup);
